/*
 * Blinkpals theme script (module, loaded once from snippets/bp-head.liquid).
 * Custom elements, so sections work again after the theme editor re-renders them:
 *   bp-eyes   the brand's dot-matrix eyes: each pupil looks at the pointer on its own (desktop), follows the scroll and
 *             the last tap (phones), jumps whole dots only, blinks every 4 to 7 s, looks around when nothing happens,
 *             squints (happy) while a [data-bp-squint] control in its section is hovered or focused
 *   bp-moods  mood chips switch the eyes' expression and the "what it really does" line; runs by itself in view
 *   bp-flip   the Mystery Buddy card: a peek when it scrolls in, flips on hover (desktop), tap or Enter
 *   bp-reel   a clip plays in place on tap, muted, one at a time
 *   bp-join-steps, bp-copy   two-step sign-up, copy a code
 * Delegated: swatches ([data-bp-sw]) swap the photo, label and add button in their card; collection sort submits.
 * In view: [data-bp-in] gets .is-in (shelf wobble), [data-bp-print] prints its characters dot by dot.
 * Guards: reduced motion, html.factory-lowfx (weak devices, Save-Data) and the motion setting (no html.bp-anim) keep
 * the eyes still and straight, with no blinking; everything moves with transform / opacity only.
 */

const root = document.documentElement;
const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const motionOn = () => root.classList.contains('bp-anim') && !reduceQuery.matches && !root.classList.contains('factory-lowfx');
const rand = (a, b) => a + Math.random() * (b - a);

/* ------------------------------------------------------------------ in view helper */
const seen = new WeakSet();
function whenInView(el, fn, opts = { threshold: 0.35 }) {
  if (!('IntersectionObserver' in window)) return fn();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        io.unobserve(e.target);
        fn();
      }
    });
  }, opts);
  io.observe(el);
}

/* ------------------------------------------------------------------ the eyes */
const EYES = new Set();
const pointer = { x: -1, y: -1, t: 0, tap: 0 };
let globalOn = false;
let frame = 0;
function tickAll() {
  frame = 0;
  EYES.forEach((e) => e.visible && e.update());
}
function requestTick() {
  if (!frame) frame = requestAnimationFrame(tickAll);
}
function ensureGlobal() {
  if (globalOn) return;
  globalOn = true;
  document.addEventListener(
    'pointermove',
    (ev) => {
      if (ev.pointerType !== 'mouse' && ev.pointerType !== 'pen') return;
      pointer.x = ev.clientX;
      pointer.y = ev.clientY;
      pointer.t = performance.now();
      requestTick();
    },
    { passive: true }
  );
  document.addEventListener(
    'pointerdown',
    (ev) => {
      pointer.x = ev.clientX;
      pointer.y = ev.clientY;
      pointer.t = performance.now();
      pointer.tap = pointer.t;
      requestTick();
    },
    { passive: true }
  );
  document.addEventListener('scroll', requestTick, { passive: true, capture: true });
  window.addEventListener('resize', requestTick);
}

/* eye centres in the SVG: viewBox width 40.5, eyes at -10.75 and +10.75 (see qa/tools/make_eyes.py) */
const EYE_X = [0.5 - 10.75 / 40.5, 0.5 + 10.75 / 40.5];

class BpEyes extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.pupils = [...this.querySelectorAll('.bp-eye__pupil')];
    this.svg = this.querySelector('svg');
    this.max = 4;
    this.cur = [
      [0, 0],
      [0, 0],
    ];
    this.saccade = [0, 0];
    this.visible = false;
    if (!motionOn()) {
      this.classList.add('is-still');
      return;
    }
    this.classList.add('is-live');
    EYES.add(this);
    ensureGlobal();
    this.io = new IntersectionObserver(([e]) => {
      this.visible = e.isIntersecting;
      if (this.visible) {
        if (!this.woke) {
          // the first time it comes into view it wakes up: a slow blink, then it looks at you
          this.woke = true;
          this.blink(420);
        }
        this.update();
        this.planBlink();
        this.planLook();
      } else {
        clearTimeout(this.bt);
        clearTimeout(this.st);
      }
    });
    this.io.observe(this);
    const scope = this.closest('.shopify-section, .cart-drawer__content, section') || this.parentElement;
    this.squinters = scope ? [...scope.querySelectorAll('[data-bp-squint]')] : [];
    this.onIn = () => this.squint(true);
    this.onOut = () => this.squint(false);
    this.squinters.forEach((el) => {
      el.addEventListener('pointerenter', this.onIn);
      el.addEventListener('pointerleave', this.onOut);
      el.addEventListener('focus', this.onIn);
      el.addEventListener('blur', this.onOut);
    });
  }

  disconnectedCallback() {
    EYES.delete(this);
    this.io?.disconnect();
    clearTimeout(this.bt);
    clearTimeout(this.st);
    this.squinters?.forEach((el) => {
      el.removeEventListener('pointerenter', this.onIn);
      el.removeEventListener('pointerleave', this.onOut);
      el.removeEventListener('focus', this.onIn);
      el.removeEventListener('blur', this.onOut);
    });
    this._on = false;
  }

  squint(on) {
    if (on) {
      if (this.dataset.squint) return;
      this.dataset.squint = '1';
      this.dataset.before = this.dataset.mood || '';
      this.dataset.mood = 'happy';
    } else if (this.dataset.squint) {
      delete this.dataset.squint;
      if (this.dataset.before) this.dataset.mood = this.dataset.before;
      else delete this.dataset.mood;
      delete this.dataset.before;
    }
  }

  planBlink() {
    clearTimeout(this.bt);
    this.bt = setTimeout(() => {
      if (!this.visible) return;
      this.blink();
      if (Math.random() < 0.22) setTimeout(() => this.blink(), 260);
      this.planBlink();
    }, rand(4000, 7000));
  }

  blink(ms = 150) {
    this.classList.add('is-blink');
    clearTimeout(this.bo);
    this.bo = setTimeout(() => this.classList.remove('is-blink'), ms);
  }

  planLook() {
    clearTimeout(this.st);
    this.st = setTimeout(() => {
      if (!this.visible) return;
      const a = rand(0, Math.PI * 2);
      const r = Math.random() < 0.3 ? 0 : rand(1.5, 3.6);
      this.saccade = [Math.cos(a) * r, Math.sin(a) * r * 0.8];
      this.update();
      this.planLook();
    }, rand(1300, 2800));
  }

  set(i, dx, dy) {
    const d = Math.hypot(dx, dy);
    if (d > this.max) {
      dx = (dx / d) * this.max;
      dy = (dy / d) * this.max;
    }
    let qx = Math.round(dx);
    let qy = Math.round(dy);
    if (Math.hypot(qx, qy) > this.max + 0.35) {
      qx = Math.trunc(dx);
      qy = Math.trunc(dy);
    }
    const c = this.cur[i];
    if (c[0] === qx && c[1] === qy) return;
    this.cur[i] = [qx, qy];
    if (this.pupils[i]) this.pupils[i].style.transform = `translate(${qx}px, ${qy}px)`;
  }

  update() {
    if (!this.svg) return;
    const r = this.svg.getBoundingClientRect();
    if (!r.width) return;
    const now = performance.now();
    const follow = this.dataset.follow;
    const fresh = now - pointer.t < (hoverQuery.matches ? 5000 : 2600) && pointer.x >= 0;
    if (follow !== 'none' && fresh && (hoverQuery.matches || now - pointer.tap < 2600)) {
      // look at the pointer (desktop) or at the last tap (phones), each eye on its own
      const reach = Math.max(180, r.width * 0.55);
      EYE_X.forEach((fx, i) => {
        const cx = r.left + r.width * fx;
        const cy = r.top + r.height / 2;
        const dx = pointer.x - cx;
        const dy = pointer.y - cy;
        const d = Math.hypot(dx, dy) || 1;
        const m = this.max * Math.min(1, d / reach);
        this.set(i, (dx / d) * m, (dy / d) * m * 0.9);
      });
      return;
    }
    if (follow !== 'none' && !hoverQuery.matches) {
      // phones: the eyes follow the scroll; the further up the page they go, the further down they look
      const vh = window.innerHeight || 800;
      const p = (r.top + r.height / 2) / vh;
      if (this.p0 === undefined) this.p0 = Math.min(0.62, p);
      const dy = Math.max(-1, Math.min(1, (this.p0 - p) * 3)) * this.max;
      const look = this.saccade[0];
      this.set(0, look, dy + this.saccade[1] * 0.4);
      this.set(1, look, dy + this.saccade[1] * 0.4);
      return;
    }
    this.set(0, this.saccade[0], this.saccade[1]);
    this.set(1, this.saccade[0], this.saccade[1]);
  }
}
if (!customElements.get('bp-eyes')) customElements.define('bp-eyes', BpEyes);

/* ------------------------------------------------------------------ moods (product page) */
class BpMoods extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.eyes = this.querySelector('bp-eyes');
    this.chips = [...this.querySelectorAll('[data-mood]')].filter((c) => c.tagName === 'BUTTON');
    this.line = this.querySelector('[data-bp-mood-line]');
    this.icon = this.querySelector('[data-bp-mood-icon]');
    this.name = this.querySelector('[data-bp-mood-name]');
    this.touched = false;
    this.chips.forEach((chip, i) => {
      chip.addEventListener('click', () => {
        this.touched = true;
        this.pick(i);
      });
      chip.addEventListener('keydown', (ev) => {
        const k = ev.key;
        if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'ArrowDown' && k !== 'ArrowUp') return;
        ev.preventDefault();
        this.touched = true;
        const n = (i + (k === 'ArrowRight' || k === 'ArrowDown' ? 1 : -1) + this.chips.length) % this.chips.length;
        this.pick(n);
        this.chips[n].focus();
      });
      chip.tabIndex = i === 0 ? 0 : -1;
    });
    this.idx = 0;
    if (motionOn() && this.dataset.auto === 'true' && this.chips.length > 1) {
      this.io = new IntersectionObserver(([e]) => {
        clearInterval(this.timer);
        if (e.isIntersecting && !this.touched) {
          this.timer = setInterval(() => {
            if (this.touched) return clearInterval(this.timer);
            this.pick((this.idx + 1) % this.chips.length);
          }, 2600);
        }
      }, { threshold: 0.4 });
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.io?.disconnect();
    this._on = false;
  }

  pick(i) {
    const chip = this.chips[i];
    if (!chip) return;
    this.idx = i;
    this.chips.forEach((c, k) => {
      c.setAttribute('aria-checked', String(k === i));
      c.tabIndex = k === i ? 0 : -1;
    });
    if (this.eyes) {
      this.eyes.dataset.mood = chip.dataset.mood;
      this.eyes.classList.remove('is-pop');
      void this.eyes.offsetWidth;
      this.eyes.classList.add('is-pop');
    }
    if (this.line) this.line.textContent = chip.dataset.line || '';
    if (this.icon && chip.dataset.icon) this.icon.setAttribute('href', `#bp-i-${chip.dataset.icon}`);
    if (this.name) this.name.textContent = chip.textContent.trim();
  }
}
if (!customElements.get('bp-moods')) customElements.define('bp-moods', BpMoods);

/* ------------------------------------------------------------------ mystery card */
class BpFlip extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.btn = this.querySelector('[data-bp-flip]');
    const toggle = (on) => {
      const v = typeof on === 'boolean' ? on : !this.classList.contains('is-flipped');
      this.classList.toggle('is-flipped', v);
      this.btn?.setAttribute('aria-expanded', String(v));
      const back = this.querySelector('.bp-char__face--back');
      const front = this.querySelector('.bp-char__face--front');
      if (back) back.inert = !v;
      if (front) front.inert = v;
      if (v) back?.querySelector('button, a[href]:not([tabindex="-1"])')?.focus({ preventScroll: true });
    };
    const back = this.querySelector('.bp-char__face--back');
    if (back) back.inert = true;
    this.btn?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      toggle(true);
    });
    this.querySelector('.bp-char__face--front')?.addEventListener('click', () => toggle(true));
    if (hoverQuery.matches) {
      this.addEventListener('pointerenter', () => toggle(true));
      this.addEventListener('pointerleave', () => toggle(false));
    }
    if (motionOn()) whenInView(this, () => setTimeout(() => this.classList.add('is-peek'), 700), { threshold: 0.6 });
  }
}
if (!customElements.get('bp-flip')) customElements.define('bp-flip', BpFlip);

/* ------------------------------------------------------------------ in view: shelf wobble, dot printing, lit buddy */
function splitPrint(el) {
  if (el.dataset.bpSplit) return;
  el.dataset.bpSplit = '1';
  const text = el.textContent;
  el.setAttribute('aria-label', text.trim());
  el.textContent = '';
  [...text].forEach((ch, i) => {
    const span = document.createElement('span');
    span.className = ch === '.' ? 'bp-print__c bp-dot' : 'bp-print__c';
    span.style.setProperty('--d', String(i));
    span.setAttribute('aria-hidden', 'true');
    span.textContent = ch;
    el.appendChild(span);
  });
}
function wireInView(scope = document) {
  scope.querySelectorAll('[data-bp-in]').forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);
    if (!motionOn()) return el.classList.add('is-in');
    el.classList.add('is-wait');
    whenInView(el, () => el.classList.add('is-in'), { threshold: 0.25 });
  });
  scope.querySelectorAll('[data-bp-print]').forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);
    if (!motionOn()) return;
    splitPrint(el);
    el.classList.add('is-wait');
    whenInView(el, () => el.classList.add('is-printed'), { threshold: 0.6 });
  });
  scope.querySelectorAll('[data-bp-lit]').forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);
    el.addEventListener('pointerenter', () => el.classList.add('is-lit'));
    el.addEventListener('pointerleave', () => el.classList.remove('is-lit'));
    el.addEventListener('focus', () => el.classList.add('is-lit'));
    el.addEventListener('blur', () => el.classList.remove('is-lit'));
    if (!hoverQuery.matches && motionOn() && 'IntersectionObserver' in window) {
      let timer = 0;
      new IntersectionObserver(([e]) => {
        clearInterval(timer);
        if (e.isIntersecting) timer = setInterval(() => el.classList.toggle('is-lit'), 2600);
        else el.classList.remove('is-lit');
      }, { threshold: 0.5 }).observe(el);
    }
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => wireInView());
else wireInView();
document.addEventListener('shopify:section:load', (ev) => wireInView(ev.target));

/* ------------------------------------------------------------------ swatches (cards, meet the hero) */
document.addEventListener('click', (event) => {
  const b = event.target.closest && event.target.closest('[data-bp-sw]');
  if (!b) return;
  const scope = b.closest('[data-bp-swatch-scope]') || b.parentElement;
  scope.querySelectorAll('[data-bp-sw]').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
  const label = scope.querySelector('[data-bp-sw-label]');
  if (label) label.textContent = b.dataset.label || '';
  const img = scope.querySelector('img.bp-swap');
  if (img && b.dataset.img) {
    img.removeAttribute('srcset');
    img.src = b.dataset.img;
  }
  scope.querySelectorAll('factory-add-button').forEach((f) => {
    if (b.dataset.variantId) f.dataset.variantId = b.dataset.variantId;
  });
  if (b.dataset.url) scope.querySelectorAll('a[href*="/products/"]').forEach((a) => (a.href = b.dataset.url));
});

/* ------------------------------------------------------------------ video slots */
class BpReel extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.bp-clip__play');
    if (!this.video || !this.button || this._on) return;
    this._on = true;
    this.button.addEventListener('click', () => (this.classList.contains('is-on') ? this.stop() : this.play()));
  }

  play() {
    document.querySelectorAll('bp-reel.is-on').forEach((r) => r !== this && r.stop && r.stop());
    this.video.muted = true;
    const started = this.video.play();
    this.classList.add('is-on');
    this.button.setAttribute('aria-pressed', 'true');
    this.button.setAttribute('aria-label', this.button.dataset.labelPause || 'Pause');
    if (started && started.catch) started.catch(() => this.stop());
  }

  stop() {
    this.video?.pause();
    this.classList.remove('is-on');
    this.button.setAttribute('aria-pressed', 'false');
    this.button.setAttribute('aria-label', this.button.dataset.labelPlay || 'Play');
  }
}
if (!customElements.get('bp-reel')) customElements.define('bp-reel', BpReel);

/* ------------------------------------------------------------------ two-step sign-up */
class BpJoinSteps extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.form = this.closest('form');
    this.mail = this.querySelector('input[type="email"]');
    this.tags = this.querySelector('[data-join-tags]');
    this.error = this.querySelector('[data-join-error]');
    this.picked = new Set();
    this.querySelector('[data-join-next]')?.addEventListener('click', () => this.next());
    this.mail?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.next();
      }
    });
    this.querySelectorAll('[data-join-tag]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        if (on) this.picked.add(chip.dataset.joinTag);
        else this.picked.delete(chip.dataset.joinTag);
        this.writeTags();
      })
    );
    this.querySelector('[data-join-skip]')?.addEventListener('click', () => {
      this.picked.clear();
      this.writeTags();
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.step(2).classList.contains('is-on')) {
        event.preventDefault();
        this.next();
      }
    });
  }

  step(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  valid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((this.mail?.value || '').trim());
  }

  next() {
    if (!this.valid()) {
      if (this.error) this.error.hidden = false;
      this.mail?.setAttribute('aria-invalid', 'true');
      this.mail?.focus();
      return;
    }
    if (this.error) this.error.hidden = true;
    this.mail?.removeAttribute('aria-invalid');
    this.step(1).classList.remove('is-on');
    this.step(2).classList.add('is-on');
    this.step(2).querySelector('button')?.focus({ preventScroll: true });
  }

  writeTags() {
    if (!this.tags) return;
    this.tags.value = [this.tags.dataset.base, ...this.picked].filter(Boolean).join(', ');
  }
}
if (!customElements.get('bp-join-steps')) customElements.define('bp-join-steps', BpJoinSteps);

/* ------------------------------------------------------------------ copy a code */
class BpCopy extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button || this._on) return;
    this._on = true;
    button.addEventListener('click', () => {
      const text = this.dataset.copy || '';
      const was = button.textContent;
      const done = () => {
        button.textContent = this.dataset.done || was;
        setTimeout(() => (button.textContent = was), 1400);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('bp-copy')) customElements.define('bp-copy', BpCopy);

/* ------------------------------------------------------------------ collection sort: submit on change */
document.addEventListener('change', (event) => {
  const select = event.target.closest && event.target.closest('[data-bp-sort] select');
  if (select) select.form.submit();
});

/* the sign-up form reloads the page after posting: bring the code into view */
if (window.location.search.includes('customer_posted=true')) {
  requestAnimationFrame(() => document.querySelector('.bp-sub__done')?.focus({ preventScroll: false }));
}

/* Menu links to a section on the same page (Gifts -> /#gifts): the header drawer stays open because the page does not
   reload. Close the drawer, then scroll to the section under the sticky header. Other pages navigate as usual. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('header-drawer a[href*="#"], .header__menu a[href*="#"]');
  if (!link) return;
  const url = new URL(link.href, location.href);
  if (url.pathname !== location.pathname || !url.hash) return;
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  if (!target) return;
  event.preventDefault();
  const drawer = link.closest('header-drawer');
  if (drawer && typeof drawer.close === 'function') drawer.close();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  setTimeout(() => {
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', url.hash);
  }, drawer ? 320 : 0);
});
